import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import KPICard from '../components/KPICard';
import TrendChart from '../components/TrendChart';
import AnomalyTable from '../components/AnomalyTable';
import {
    getDashboardStats,
    getDashboardKPIs,
    getKPITrend,
    getAnomalies,
    generateDemoData,
    checkData,
    exportReport
} from '../lib/api';

export const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [anomalies, setAnomalies] = useState([]);
    const [selectedKPI, setSelectedKPI] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendLoading, setTrendLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [hasData, setHasData] = useState(true);

    const loadDashboard = useCallback(async () => {
        try {
            setLoading(true);
            
            const dataCheck = await checkData();
            setHasData(dataCheck.data.has_data);
            
            if (!dataCheck.data.has_data) {
                setLoading(false);
                return;
            }

            const [statsRes, kpisRes, anomaliesRes] = await Promise.all([
                getDashboardStats(),
                getDashboardKPIs(),
                getAnomalies({ limit: 10 })
            ]);

            setStats(statsRes.data);
            setKpis(kpisRes.data);
            setAnomalies(anomaliesRes.data);

            if (kpisRes.data.length > 0) {
                const firstKPI = kpisRes.data[0];
                setSelectedKPI(firstKPI);
                loadTrendData(firstKPI.id);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTrendData = async (kpiId, days = 30) => {
        try {
            setTrendLoading(true);
            const response = await getKPITrend(kpiId, days);
            setTrendData(response.data.data);
        } catch (error) {
            console.error('Failed to load trend data:', error);
        } finally {
            setTrendLoading(false);
        }
    };

    const handleGenerateDemo = async () => {
        try {
            setGenerating(true);
            toast.info('Generating demo data...', { duration: 10000 });
            
            await generateDemoData(90);
            
            toast.success('Demo data generated successfully!');
            setHasData(true);
            await loadDashboard();
        } catch (error) {
            console.error('Failed to generate demo data:', error);
            toast.error('Failed to generate demo data');
        } finally {
            setGenerating(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await exportReport();
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kpi-report-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Report exported successfully!');
        } catch (error) {
            console.error('Failed to export report:', error);
            toast.error('Failed to export report');
        }
    };

    const handleKPISelect = (kpi) => {
        setSelectedKPI(kpi);
        loadTrendData(kpi.id);
    };

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    if (!hasData && !loading) {
        return (
            <div className="animate-fade-in">
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                    <div className="glass-card p-12 max-w-lg">
                        <AlertCircle className="h-16 w-16 text-primary mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-card-foreground mb-3">No Data Available</h2>
                        <p className="text-muted-foreground mb-8">
                            Generate demo data to explore KPI Watchtower's anomaly detection capabilities with a simulated ecommerce dataset.
                        </p>
                        <Button 
                            onClick={handleGenerateDemo}
                            disabled={generating}
                            className="btn-glow"
                            size="lg"
                            data-testid="generate-demo-data-btn"
                        >
                            {generating ? (
                                <>
                                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-5 w-5 mr-2" />
                                    Generate Demo Data
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-card-foreground">Dashboard</h1>
                    <p className="text-muted-foreground mt-1">Monitor your key performance indicators</p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={loadDashboard}
                        disabled={loading}
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button 
                        variant="outline"
                        onClick={handleExport}
                        data-testid="export-btn"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                    <Button 
                        onClick={handleGenerateDemo}
                        disabled={generating}
                        className="btn-glow"
                        data-testid="generate-demo-btn"
                    >
                        {generating ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        Generate Demo
                    </Button>
                </div>
            </div>

            {/* Stats Overview */}
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-24" />
                    ))}
                </div>
            ) : stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <p className="label-text">Total KPIs</p>
                            <p className="text-3xl font-mono font-bold text-card-foreground mt-2">{stats.total_kpis}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <p className="label-text">Anomalies</p>
                            <p className="text-3xl font-mono font-bold text-orange-400 mt-2">{stats.total_anomalies}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <p className="label-text">Critical</p>
                            <p className="text-3xl font-mono font-bold text-destructive mt-2">{stats.critical_anomalies}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="pt-6">
                            <p className="label-text">Unread Alerts</p>
                            <p className="text-3xl font-mono font-bold text-primary mt-2">{stats.unread_alerts}</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* KPI Cards */}
            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {kpis.map((kpi) => (
                        <KPICard 
                            key={kpi.id} 
                            kpi={kpi} 
                            onClick={() => handleKPISelect(kpi)}
                        />
                    ))}
                </div>
            )}

            {/* Trend Chart & Anomalies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedKPI && (
                    <TrendChart
                        data={trendData}
                        kpiName={selectedKPI.name}
                        unit={selectedKPI.unit}
                        loading={trendLoading}
                        onRangeChange={(days) => loadTrendData(selectedKPI.id, days)}
                    />
                )}
                <AnomalyTable anomalies={anomalies} />
            </div>
        </div>
    );
};

export default Dashboard;
